import dayjs from "dayjs";

import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";

const SAUDI_NAMES = ["Saudi Arabia", "المملكة العربية السعودية"];

export const isSaudiNationality = (nationality) =>
  SAUDI_NAMES.includes(nationality?.name);

export const formatProfileDate = (value, lang) => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid()
    ? parsed.locale(lang === "ar" ? "ar" : "en").format("DD MMMM YYYY")
    : String(value);
};

export const isExpiredDate = (value) => {
  if (!value) return false;
  const parsed = dayjs(value);
  return parsed.isValid() && parsed.endOf("day").isBefore(dayjs().startOf("day"));
};

// Prefer the stored age; otherwise derive it from the Gregorian date of birth.
export const getApplicantAge = (applicant) => {
  const age = applicant?.age;
  if (age != null && age !== "") return age;
  const dob = applicant?.gregorian_date_of_birth;
  if (!dob) return null;
  const parsed = dayjs(dob);
  if (!parsed.isValid()) return null;
  const years = dayjs().diff(parsed, "year");
  return years >= 0 ? years : null;
};

const hasDocuments =(items) => Array.isArray(items) && items.length > 0;

const toDocumentList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return [value];
};

export const resolveDocumentUrl = (document) => {
  if (!document) return "";
  if (typeof document === "string") return document;
  if (typeof document === "object") {
    return document.url ?? document.file_url ?? document.href ?? "";
  }
  return "";
};

export const getCertificateDocuments = (applicant) => {
  const data = applicant ?? {};
  return [
    ...toDocumentList(data.safety_and_security_documents),
    ...toDocumentList(data.step_tofel_ielts_documents),
  ];
};

const readAttr = (node, key) => node?.[key] ?? node?.attributes?.[key];

export const normalizeApplicantProfile = (user) => {
  if (!user || typeof user !== "object") return null;

  const attrs =
    user.attributes && typeof user.attributes === "object"
      ? { ...user.attributes }
      : { ...user };

  const id = user.id ?? attrs.id;
  const nationality = attrs.nationality;
  const role = attrs.role;

  return {
    ...attrs,
    id,
    nationality:
      nationality && typeof nationality === "object"
        ? {
            ...nationality,
            id: readAttr(nationality, "id") ?? nationality.id,
            name: readAttr(nationality, "name") ?? nationality.name,
          }
        : nationality,
    role:
      role && typeof role === "object"
        ? {
            ...role,
            id: readAttr(role, "id") ?? role.id,
            name: readAttr(role, "name") ?? role.name,
          }
        : role,
    user_languages: Array.isArray(attrs.user_languages)
      ? attrs.user_languages
      : [],
    cv_documents: toDocumentList(attrs.cv_documents),
    academic_qualification_documents: toDocumentList(
      attrs.academic_qualification_documents,
    ),
    other_documents: toDocumentList(attrs.other_documents),
    safety_and_security_documents: toDocumentList(
      attrs.safety_and_security_documents,
    ),
    step_tofel_ielts_documents: toDocumentList(attrs.step_tofel_ielts_documents),
  };
};

export const computeProfileCompleteness = (applicant, labels) => {
  const data = applicant ?? {};
  const isSaudi = isSaudiNationality(data.nationality);
  const checks = [
    {
      key: "age",
      label: labels.age,
      complete: getApplicantAge(data) != null,
    },
    {
      key: "dateOfBirth",
      label: labels.dateOfBirth,
      complete: Boolean(
        data.gregorian_date_of_birth || data.hijri_date_of_birth,
      ),
    },
    {
      key: "gender",
      label: labels.gender,
      complete: Boolean(data.gender),
    },
    {
      key: "companyWorkedWith",
      label: labels.companyWorkedWith,
      complete: Boolean(data.favourite_company?.name),
    },
    {
      key: "certificates",
      label: labels.certificates,
      complete: hasDocuments(getCertificateDocuments(data)),
    },
    {
      key: "academic",
      label: labels.academicQualifications,
      complete: hasDocuments(data.academic_qualification_documents),
    },
    {
      key: "cv",
      label: labels.cv,
      complete: hasDocuments(data.cv_documents),
    },
  ];

  // Required checks mirror EditApplicant.validation.js; everything else is optional.
  const { basic, bank, education } = labels;
  const hasText = (value) => value != null && String(value).trim() !== "";
  const hasId = (option) => option?.id != null;
  const requiredChecks = [
    [basic.firstName, hasText(data.first_name_en || data.first_name)],
    [basic.middleName, hasText(data.middle_name_en || data.middle_name)],
    [basic.lastName, hasText(data.last_name_en || data.last_name)],
    [basic.firstNameAr, hasText(data.first_name_ar || data.first_name)],
    [basic.middleNameAr, hasText(data.middle_name_ar || data.middle_name)],
    [basic.lastNameAr, hasText(data.last_name_ar || data.last_name)],
    [basic.nationality, hasId(data.nationality)],
    [basic.cityOfResidence, hasId(data.city)],
    [basic.countryCode, hasText(data.country_code)],
    [basic.phoneNumber, hasText(data.phone_number)],
    [basic.role, hasId(data.role)],
    [basic.height, hasText(data.height)],
    [basic.weight, hasText(data.weight)],
    [basic.stcNumber, hasText(data.stc_number)],
    [basic.notifiable, data.is_notifiable != null],
    [bank.bankName, hasId(data.bank)],
    [bank.IBAN, hasText(data.iban)],
    [bank.cardHolder, hasText(data.card_holder_name)],
    [education.filedsOfStudy, hasId(data.field_of_study)],
  ];
  if (data.field_of_study?.name === "Other") {
    requiredChecks.push([education.fieldOfStudyName, hasText(data.field_of_study_name)]);
  }
  if (isSaudi) {
    requiredChecks.push(
      [basic.nationalId, hasText(data.national_id)],
      [basic.expiryDate, hasText(data.national_id_expiration_date)],
    );
  } else {
    requiredChecks.push(
      [basic.iqamaNumber, hasText(data.iqama_id)],
      [basic.iqmaExpireDate, hasText(data.iqama_expiration_date)],
    );
  }
  requiredChecks.forEach(([label, complete]) =>
    checks.push({ key: label, label, required: true, complete }),
  );

  if (!isSaudi) {
    checks.push({
      key: "matloobNumber",
      label: labels.matloobNumber,
      complete: Boolean(data.matloob_number),
    });
  }

  const completeCount = checks.filter((item) => item.complete).length;
  const percent = Math.round((completeCount / checks.length) * 100);
  const missing = checks.filter((item) => !item.complete);
  const missingFields = missing
    .filter((item) => item.required)
    .map((item) => item.label);
  const missingOptionalFields = missing
    .filter((item) => !item.required)
    .map((item) => item.label);

  if (
    !isSaudi &&
    data.iqama_id &&
    isExpiredDate(data.iqama_expiration_date) &&
    !missingFields.includes(labels.iqamaExpired)
  ) {
    missingFields.push(labels.iqamaExpired);
  }

  return {
    percent,
    missingCount: missingFields.length + missingOptionalFields.length,
    missingFields,
    missingOptionalFields,
  };
};

export const readApplicantDisplayName = (applicant, lang) =>
  applicant?.[`full_name_${lang}`] ||
  applicant?.name ||
  applicant?.full_name_en ||
  applicant?.full_name_ar ||
  "";

export const readUserLanguageName = (item) => {
  const raw = item?.language;
  if (typeof raw === "string") return raw;
  if (raw && typeof raw === "object") {
    return raw.name ?? raw.name_en ?? raw.name_ar ?? "";
  }
  return item?.language_name ?? "";
};

export const readUserLanguageId = (item) => {
  const raw = item?.language;
  if (raw && typeof raw === "object") return raw.id ?? null;
  return item?.language_id ?? null;
};

export const readUserLanguageProficiencyLabel = (item, lang) =>
  (lang === "ar" ? item?.localized_speaking_proficiency : item?.speaking_proficiency) ||
  item?.speaking_proficiency ||
  item?.localized_speaking_proficiency ||
  "";

export const getApplicantProfileRoutes = (portalVariant, id) => {
  const isAdminPortal = portalVariant === "admin-portal";

  return {
    applicantsList: isAdminPortal
      ? ROUTE_PATHS.applicants
      : ROUTE_PATHS.companyApplicants,
    edit: (isAdminPortal
      ? ROUTE_PATHS.editApplicantProfile
      : ROUTE_PATHS.companyEditApplicantProfile
    ).replace(":id", id),
    view: (isAdminPortal
      ? ROUTE_PATHS.viewApplicant
      : ROUTE_PATHS.companyViewApplicantProfile
    ).replace(":id", id),
  };
};
